/** @format */

import { CommonModule, DOCUMENT } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { chain } from '@core/chain';
import { Store } from '@ngrx/store';
import { BVPixelResponse, InvalidResponse, Review, selectBVPixel } from '@product-reviews/data-access';

import {
	BVEventObject,
	BVEventObjectName,
	BVEventObjectType,
	BVEventType,
	BVProduct
} from '../models';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import { HighlightKeywordDirective } from '../directive/highlight-keyword/highlight-keyword.directive';

@Component({
	selector: 'product-reviews-review-text',
	templateUrl: './review-text.component.html',
	styleUrl: './review-text.component.scss',
	imports: [CommonModule, HighlightKeywordDirective, HmfActionTextComponent],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewTextComponent {
	readonly review = input<Review>();
	readonly highlightedWord = input<string>();

	private readonly document = inject(DOCUMENT);
	private readonly store = inject(Store<BVPixelResponse>);

	readonly paragraphElement = signal<HTMLElement>(null);
	readonly buttonElement = signal<HTMLElement>(null);
	readonly bvPixelData = this.store.selectSignal(selectBVPixel);
	readonly bvPixelResponse = computed(() => {
		const bvPixel = this.bvPixelData();
		return bvPixel instanceof InvalidResponse ? null : bvPixel;
	});
	readonly chainAbbr = signal(chain.chainIdentifierAbbr);

	constructor() {
		afterNextRender(() => {
			const reviewId = this.review()?.Id;
			if (!reviewId) return;

			const paragraphEl = this.document.querySelector<HTMLElement>(`#reviewText_${reviewId}`);
			this.paragraphElement.set(paragraphEl);

			if (!paragraphEl) {
				return;
			}

			const showMore = paragraphEl.scrollHeight > paragraphEl.clientHeight;

			if (showMore) {
				const buttonEl = this.document.querySelector<HTMLElement>(`#viewMoreButton_${reviewId}`);
				this.buttonElement.set(buttonEl);
				buttonEl?.classList.add('enabled');
			}
		});
	}

	handleBVPixel(): void {
		const bvPixel = this.bvPixelResponse();

		if (!bvPixel || !BV.pixel) {
			return;
		}

		const bvObject: BVEventObject = {
			type: BVEventObjectType.USED,
			name: BVEventObjectName.READMORE,
			brand: bvPixel.brand,
			productId: bvPixel.productId,
			bvProduct: BVProduct.RATINGSANDREVIEWS,
			categoryId: bvPixel.category,
			detail1: 'expand',
			detail2: 'mainReviews'
		};

		BV.pixel.trackEvent(BVEventType.FEATURE, bvObject);
	}

	toggleExpandedReview(): void {
		this.handleBVPixel();
		this.buttonElement()?.classList.remove('enabled');
		this.paragraphElement()?.classList.remove('line-clamp');
	}
}
